import React from 'react'
import { Box, BoxProps } from '@mui/material'
import { styled } from '@mui/material/styles'

export interface ChipToggleOption {
  value: string
  label: string
  disabled?: boolean
}

export interface ChipToggleGroupProps extends Omit<BoxProps, 'onChange' | 'children'> {
  options: ChipToggleOption[]
  value: string
  onChange: (value: string) => void
}

const Row = styled(Box)({
  display: 'flex',
  flexWrap: 'wrap',
  gap: 'var(--ds-space-2)',
})

const Chip = styled('button', {
  shouldForwardProp: (p) => p !== '$active',
})<{ $active?: boolean }>(({ $active }) => ({
  all: 'unset',
  boxSizing: 'border-box',
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  height: 30,
  padding: '0 13px',
  borderRadius: 'var(--ds-radius-pill)',
  fontSize: 12.5,
  fontWeight: 600,
  letterSpacing: '-0.01em',
  background: $active ? 'var(--ds-inverse)' : 'var(--ds-hover)',
  color: $active ? 'var(--ds-inverse-ink)' : 'var(--ds-ink-muted)',
  transition: `background var(--ds-duration-fast) var(--ds-ease), color var(--ds-duration-fast) var(--ds-ease)`,
  '&:hover:not([disabled])': {
    color: $active ? 'var(--ds-inverse-ink)' : 'var(--ds-ink)',
  },
  '&[disabled]': {
    opacity: 0.4,
    cursor: 'not-allowed',
  },
  '&:focus-visible': {
    outline: '2px solid var(--ds-ink)',
    outlineOffset: 2,
  },
}))

/** Pill-row single-select toggle — matting width presets, sampling modes, etc. */
export function ChipToggleGroup({ options, value, onChange, ...rest }: ChipToggleGroupProps) {
  return (
    <Row role='radiogroup' {...rest}>
      {options.map((opt) => (
        <Chip
          key={opt.value}
          type='button'
          role='radio'
          aria-checked={value === opt.value}
          $active={value === opt.value}
          disabled={opt.disabled}
          onClick={() => onChange(opt.value)}
        >
          {opt.label}
        </Chip>
      ))}
    </Row>
  )
}
